<template>
  <b-container class="credit-product-parameters">
    <b-button
      variant="outline-secondary"
      class="back-button"
      @click="goBack"
    >
      <feather-icon
        icon="ArrowLeftIcon"
        size="16"
        class="mr-50"
      />
      Voltar
    </b-button>

    <b-alert
      v-if="hasUnsavedChanges"
      id="unsaved-changes-alert"
      show
      class="unsaved-changes-alert"
      variant="warning"
    >
      <div class="unsaved-alert-content">
        <span>Existem alterações não salvas.</span>

        <div class="unsaved-alert-actions">
          <b-button
            size="sm"
            variant="primary"
            @click.stop="save"
          >
            Salvar alterações
          </b-button>

          <b-button
            size="sm"
            variant="outline-primary"
            @click.stop="discardChanges"
          >
            Descartar alterações
          </b-button>
        </div>
      </div>
    </b-alert>

    <credit-product-selector
      v-model="params.productId"
      :options="creditProducts"
      @search="getData"
    />

    <b-card
      v-if="queue.length"
      class="queue-card"
    >
      <h4 class="queue-title">
        Ordem de análise
      </h4>

      <draggable
        v-model="queue"
        class="queue-list"
        handle=".queue-item"
        :animation="150"
        @change="markUnsaved"
      >
        <span
          v-for="(step, index) in queue"
          :key="`${step}-${index}`"
          class="queue-item"
          title="Arraste para reordenar"
        >
          <span class="queue-index">
            {{ index + 1 }}
          </span>

          {{ formatQueueStep(step) }}
        </span>
      </draggable>
    </b-card>
  <b-card v-if="Object.keys(parameters).length">
    <div
      v-if="Object.keys(parameters).length"
      class="parameters-container"
    >
      <credit-product-parameter-group
        v-for="parameter in parameterOrder"
        :key="parameter"
        :parameter="parameter"
        :values="parameters[parameter]"
        @input="handleParameterChange"
      />

      <div
        id="save-parameters"
        class="save-container"
      >
        <b-button
          variant="primary"
          :disabled="saving"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          Salvar
        </b-button>
      </div>
    </div>
    </b-card>
  </b-container>
</template>

<script>
import {
  BContainer,
  BButton,
  BCard,
  BAlert,
  BSpinner
} from 'bootstrap-vue'
import draggable from 'vuedraggable'

import CreditProductSelector from './components/CreditProductParameters/CreditProductSelector.vue'
import CreditProductParameterGroup from './components/CreditProductParameters/CreditProductParameterGroup.vue'
import CreditAnalysisService from '@/service/credit-analysis'
import { callLoading } from '@/service/utilities'

export default {
  name: 'CreditProductParameters',

  components: {
    BContainer,
    BButton,
    BCard,
    BAlert,
    BSpinner,
    draggable,
    CreditProductSelector,
    CreditProductParameterGroup
  },

  data() {
    return {
      params: {
        productId: null
      },
      creditProducts: [],
      parameters: {},
      queue: [],
      originalParameters: {},
      originalQueue: [],

      hasUnsavedChanges: false,
      saving: false,

      parameterOrder: [
        'internal_history',
        'beecred',
        'process',
        'pgfn',
        'pge',
        'serasa',
        'scr',
        'blacklist'
      ]
    }
  },

  async mounted() {
    callLoading(true)

    try {
      const response = await CreditAnalysisService.getProducts()
      const data = response && response.data
      const products = data && (data.data || data)

      this.creditProducts = Array.isArray(products)
        ? products.map(product => ({
          value: product.value || product.id,
          text: product.text || product.name
        }))
        : []
    } catch (error) {
      this.$toast.error('Não foi possível carregar os produtos de crédito.')
    } finally {
      callLoading(false)
    }
  },

  methods: {
    async getData() {
      callLoading(true)
      this.hasUnsavedChanges = false

      try {
        const response = await CreditAnalysisService.getParameters(
          this.params.productId
        )
        const data = response && response.data
        const result = data && (data.data || data)

        this.parameters = result && result.parameters
          ? result.parameters
          : {}

        this.queue = result && Array.isArray(result.queue)
          ? result.queue
          : []

        this.originalParameters = this.cloneState(this.parameters)
        this.originalQueue = this.cloneState(this.queue)
        this.hasUnsavedChanges = false
      } catch (error) {
        this.$toast.error('Não foi possível carregar os parâmetros.')
      } finally {
        callLoading(false)
      }
    },

    handleParameterChange({
      parameter,
      field,
      value
    }) {
      if (!this.parameters[parameter]) {
        this.$set(this.parameters, parameter, {})
      }

      this.$set(
        this.parameters[parameter],
        field,
        value
      )

      this.markUnsaved()
    },

    cloneState(value) {
      return JSON.parse(JSON.stringify(value || {}))
    },

    hasChanges() {
      return JSON.stringify(this.parameters) !== JSON.stringify(this.originalParameters)
        || JSON.stringify(this.queue) !== JSON.stringify(this.originalQueue)
    },

    markUnsaved() {
      this.hasUnsavedChanges = this.hasChanges()
    },

    discardChanges() {
      this.parameters = this.cloneState(this.originalParameters)
      this.queue = this.cloneState(this.originalQueue)
      this.hasUnsavedChanges = false
    },

    async goBack() {
      if (!this.hasUnsavedChanges) {
        this.$router.go(-1)
        return
      }

      const result = await this.$swal({
        title: 'Alterações não salvas',
        text: 'Você tem alterações não salvas. O que deseja fazer?',
        icon: 'warning',

        showDenyButton: true,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonText: 'Salvar e voltar',
        denyButtonText: 'Voltar sem salvar',

        reverseButtons: true,

        focusConfirm: false,
        focusDeny: false,

        didOpen: () => {
          const actions = document.querySelector('.swal2-actions')

          if (actions) {
            actions.style.gap = '20px'
          }
          document.activeElement?.blur()
        }
      })

      if (result.isConfirmed) {
        await this.save(true)
        return
      }

      if (result.isDenied) {
        this.discardChanges()
        this.$router.go(-1)
      }
    },

    formatQueueStep(step) {
      const names = {
        Blacklist: 'Blacklist',
        InternalHistory: 'Histórico Interno',
        Beecred: 'Anotações de Mercado',
        Process: 'Processos',
        Pgfn: 'PGFN',
        Pge: 'PGE',
        Serasa: 'Restrições',
        Scr: 'SCR',
        Finish: 'Finalização'
      }

      return names[step] || step
    },

    async save(shouldGoBack = false) {
      this.saving = true
      callLoading(true)

      try {
        const response = await CreditAnalysisService.updateParameters({
          product_id: this.params.productId,
          user: this.$store.state.user.data.name,
          parameters: this.parameters,
          queue: this.queue
        })

        if (response && response.status >= 200 && response.status < 300) {
          this.originalParameters = this.cloneState(this.parameters)
          this.originalQueue = this.cloneState(this.queue)
          this.hasUnsavedChanges = false
          this.$toast.success('Parâmetros salvos com sucesso.')

          if (shouldGoBack) {
            this.$router.go(-1)
          }
        } else {
          this.$toast.error('Não foi possível salvar os parâmetros.')
        }
      } catch (error) {
        this.$toast.error('Não foi possível salvar os parâmetros.')
      } finally {
        this.saving = false
        callLoading(false)
      }
    }
  }
}
</script>

<style scoped>
.credit-product-parameters {
  padding-top: 20px;
  padding-bottom: 30px;
}

.back-button {
  margin-bottom: 16px;
}

.unsaved-changes-alert {
  cursor: pointer;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #f0d98c;
  border-left: 4px solid #e0b341;
  border-radius: 6px;
  background-color: #fff9e6 !important;
  color: #6b5a2b !important;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.unsaved-changes-alert:hover {
  background-color: #fff6d6 !important;
  border-color: #e5c76f;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.06);
}

.unsaved-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.unsaved-alert-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.queue-card {
  border: 0;
  border-radius: 8px;
  margin-top: 16px;
}

.queue-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 18px;
}

.queue-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.queue-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
  cursor: grab;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.08);
}

.queue-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #393273;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.parameters-container {
  margin-top: 24px;
  border-radius: 5px;
  padding: 24px;
}

.save-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

@media (max-width: 768px) {
  .parameters-container {
    padding: 16px;
  }

  .queue-list {
    flex-direction: column;
  }

  .queue-item {
    width: 100%;
  }
}
</style>